/**
 * Picks nodes of the catalog itself: topics, capabilities, or both.
 *
 * This is the counterpart to `TreeSelect`, and the difference is what a tick
 * means. There, ticking a topic is shorthand for every question of it. Here a
 * tick means that one node. Nothing rolls up and nothing rolls down, because
 * what a list means is the consumer's business, not the picker's.
 */
import { For, Show, createMemo } from "solid-js";
import { useI18n } from "../i18n/provider.tsx";
import {
  TreeFrame,
  Twisty,
  showsBranch,
  treeStyles as styles,
  useFolding,
} from "./tree-shell.tsx";
import type { TreeTopic } from "../api/domain.ts";

/** The kinds of node a tick can land on. */
export type ChooseKind = "topic" | "capability";

/** One picked node. The kind matters: a topic and a capability may share an id. */
export type ChosenNode = { kind: ChooseKind; id: string };

export type TreeChooseProps = {
  topics: TreeTopic[];
  /**
   * Which kinds may be ticked. A kind left out still renders, greyed, because
   * seeing where a capability sits is what makes its topic worth picking.
   */
  kinds: readonly ChooseKind[];
  chosen: readonly ChosenNode[];
  onChange: (chosen: ChosenNode[]) => void;
  /**
   * At most one node at a time, which is what an owning-topic field needs. The
   * control becomes a radio, so the shape tells the user the rule before they
   * hit it.
   */
  single?: boolean;
  /**
   * Names the empty selection. A radio cannot be unticked by clicking it, so
   * without this a single-select field could never be returned to nothing.
   */
  clearLabel?: string;
};

const keyOf = (node: ChosenNode): string => `${node.kind}:${node.id}`;

export const TreeChoose = (props: TreeChooseProps) => {
  const i18n = useI18n();
  const folding = useFolding(
    () => props.topics,
    (topic) => topic.capabilities.length > 0,
  );

  const chosen = createMemo(() => new Set(props.chosen.map(keyOf)));
  const takes = (kind: ChooseKind) => props.kinds.includes(kind);

  /** Every pickable node in catalog order, which is the order a change is emitted in. */
  const offered = createMemo<ChosenNode[]>(() =>
    props.topics.flatMap((topic) => [
      ...(takes("topic") ? [{ kind: "topic" as const, id: topic.id }] : []),
      ...(takes("capability")
        ? topic.capabilities.map((capability) => ({
            kind: "capability" as const,
            id: capability.id,
          }))
        : []),
    ]),
  );

  const emit = (next: ReadonlySet<string>) =>
    // Catalog order, so what the caller stores does not depend on click order.
    props.onChange(offered().filter((node) => next.has(keyOf(node))));

  const toggle = (node: ChosenNode, on: boolean) => {
    if (props.single === true) {
      props.onChange(on ? [node] : []);
      return;
    }
    const next = new Set(chosen());
    if (on) next.add(keyOf(node));
    else next.delete(keyOf(node));
    emit(next);
  };

  const Pick = (own: { node: ChosenNode; label: string; titleClass?: string }) => {
    const on = () => chosen().has(keyOf(own.node));

    return (
      <label class={styles.pick}>
        <input
          type={props.single === true ? "radio" : "checkbox"}
          checked={on()}
          onChange={(event) => toggle(own.node, event.currentTarget.checked)}
        />
        <span class={own.titleClass}>{own.label}</span>
      </label>
    );
  };

  const Node = (branch: { topic: TreeTopic }) => (
    <li class={styles.node}>
      <div class={styles.row}>
        <Twisty topic={branch.topic} folding={folding} />
        <Show
          when={takes("topic")}
          fallback={<span class={styles.topicTitle}>{branch.topic.title}</span>}
        >
          <Pick
            node={{ kind: "topic", id: branch.topic.id }}
            label={branch.topic.title}
            titleClass={styles.topicTitle}
          />
        </Show>
      </div>

      <Show when={showsBranch(branch.topic, folding)}>
        <ul class={styles.branch}>
          <Show when={takes("capability")}>
            <For each={branch.topic.capabilities}>
              {(capability) => (
                <li class={styles.leaf}>
                  <div class={styles.row}>
                    <span class={styles.twistyGap} aria-hidden="true" />
                    <Pick
                      node={{ kind: "capability", id: capability.id }}
                      label={capability.label}
                      titleClass={styles.questionTitle}
                    />
                  </div>
                </li>
              )}
            </For>
          </Show>
        </ul>
      </Show>
    </li>
  );

  const summary = () =>
    props.single === true
      ? (props.chosen[0]?.id ?? i18n.t().common.none)
      : `${props.chosen.length} ${i18n.t().common.of} ${offered().length} ${i18n.t().tree.selected}`;

  return (
    <TreeFrame
      folding={folding}
      filled={props.topics.length > 0}
      summary={summary()}
      lead={
        <button
          class={styles.clear}
          type="button"
          disabled={props.chosen.length === 0}
          onClick={() => props.onChange([])}
        >
          {props.clearLabel ?? i18n.t().tree.clear}
        </button>
      }
    >
      <For each={props.topics}>{(topic) => <Node topic={topic} />}</For>
    </TreeFrame>
  );
};
